/**
 * Color Range — generate a selection mask from an ImageData source
 * by per-pixel color / luminance classification.
 *
 * Implements two flavors:
 *   1. `'sampled'` — user picks one or more reference colors; the
 *      pixel's HSL-space distance from the nearest sample is
 *      compared against a fuzziness threshold with a soft falloff.
 *   2. Preset bands — fixed hue / luminance ranges (Reds, Yellows,
 *      Greens, Cyans, Blues, Magentas, Highlights, Midtones,
 *      Shadows). No sample needed.
 *
 * Output is `Uint8Array(w*h)` with values 0..255 — a SOFT mask so
 * the modal can show a real grayscale preview. Callers that need a
 * binary mask threshold at 127 (matches the rest of the editor's
 * mask code; see `canvasToBinaryMask`).
 */

export type ColorRangeMode =
  | 'sampled'
  | 'reds'
  | 'yellows'
  | 'greens'
  | 'cyans'
  | 'blues'
  | 'magentas'
  | 'highlights'
  | 'midtones'
  | 'shadows';

export interface RgbSample {
  r: number;
  g: number;
  b: number;
}

export interface ColorRangeOptions {
  mode: ColorRangeMode;
  /** Sample colors (sRGB 0..255). Required for 'sampled' mode;
   *  ignored otherwise. */
  samples: RgbSample[];
  /** Photoshop's "Fuzziness", 0..200. For sampled mode this maps to
   *  the HSL-space distance threshold; for preset bands it widens
   *  the band's falloff. 0 = exact match only. 200 = very loose. */
  fuzziness: number;
}

/** Per-pixel mask values (0..255). 0 = not in range, 255 = fully
 *  in range, intermediate = partial membership (used by the falloff
 *  edges).
 *
 *  We deliberately don't return ImageData here — the modal converts
 *  to grayscale ImageData for preview, but downstream code that
 *  installs a selection just needs the 0/1 threshold. */
export function buildColorRangeMask(source: ImageData, opts: ColorRangeOptions): Uint8Array {
  const data = source.data;
  const w = source.width;
  const h = source.height;
  const out = new Uint8Array(w * h);

  if (opts.mode === 'sampled') {
    return buildSampledMask(data, out, opts.samples, opts.fuzziness);
  }
  if (opts.mode === 'highlights' || opts.mode === 'midtones' || opts.mode === 'shadows') {
    return buildLuminanceBandMask(data, out, opts.mode, opts.fuzziness);
  }
  return buildHueBandMask(data, out, opts.mode, opts.fuzziness);
}

/** Convert a soft-mask Uint8Array (0..255) into an ImageData
 *  grayscale preview suitable for canvas drawImage. White = in
 *  range. */
export function softMaskToImageData(mask: Uint8Array, width: number, height: number): ImageData {
  const img = new ImageData(width, height);
  const d = img.data;
  for (let i = 0; i < mask.length; i++) {
    const v = mask[i];
    const o = i * 4;
    d[o] = v;
    d[o + 1] = v;
    d[o + 2] = v;
    d[o + 3] = 255;
  }
  return img;
}

/** Sample-based selection. For each pixel compute the minimum
 *  HSL-space distance to any sample, then map through a tent
 *  falloff centered at the threshold (fuzziness/200 → t in [0,1]).
 *
 *  HSL distance is computed as a weighted sum: hue diff (wrapped at
 *  180°, normalized to [0,1]), saturation diff, luminance diff.
 *  Weights chosen so visually-similar colors land close (luma weighed
 *  highest because human perception is most sensitive to it). */
function buildSampledMask(
  data: Uint8ClampedArray,
  out: Uint8Array,
  samples: RgbSample[],
  fuzziness: number,
): Uint8Array {
  if (samples.length === 0) return out;
  // Precompute samples in HSL.
  const sampleHsl = samples.map((s) => rgbToHsl(s.r, s.g, s.b));
  // Threshold in [0..1]. fuzziness 0 → very tight (0.02 base);
  // fuzziness 200 → very loose (1.0). Quadratic curve so the slider
  // feels right at low values.
  const t = fuzziness / 200;
  const threshold = 0.02 + t * t * 0.98;
  const softBand = Math.max(0.001, threshold * 0.5);

  const len = out.length;
  for (let i = 0; i < len; i++) {
    const di = i * 4;
    const hsl = rgbToHslFast(data[di], data[di + 1], data[di + 2]);
    let minDist = Infinity;
    for (let s = 0; s < sampleHsl.length; s++) {
      const sH = sampleHsl[s];
      const dist = hslDistance(hsl[0], hsl[1], hsl[2], sH[0], sH[1], sH[2]);
      if (dist < minDist) minDist = dist;
    }
    if (minDist <= threshold) {
      out[i] = 255;
    } else if (minDist <= threshold + softBand) {
      const tT = (minDist - threshold) / softBand;
      out[i] = Math.round((1 - tT) * 255);
    } else {
      out[i] = 0;
    }
  }
  return out;
}

/** Preset hue-band selection (Reds/Yellows/Greens/Cyans/Blues/
 *  Magentas). Photoshop's classic 6-band split centered at every
 *  60° around the hue wheel, with a falloff at the band edges that
 *  fuzziness widens.
 *
 *  We use sat-aware gating: nearly-gray pixels (sat < 0.05) never
 *  contribute to any band — otherwise the "Reds" band would pick up
 *  black/white/gray, which is never what users want. */
function buildHueBandMask(
  data: Uint8ClampedArray,
  out: Uint8Array,
  mode: ColorRangeMode,
  fuzziness: number,
): Uint8Array {
  const center = HUE_BAND_CENTERS[mode] ?? 0;
  // Base half-width 30°; fuzziness widens up to 60° at max.
  const halfBand = 30 + (fuzziness / 200) * 30;
  // Falloff width — fuzziness also widens the soft edge.
  const falloff = 5 + (fuzziness / 200) * 25;

  const len = out.length;
  for (let i = 0; i < len; i++) {
    const di = i * 4;
    const hsl = rgbToHslFast(data[di], data[di + 1], data[di + 2]);
    if (hsl[1] < 0.05) {
      out[i] = 0;
      continue;
    }
    const hueDiff = hueDistanceDeg(hsl[0] * 360, center);
    if (hueDiff <= halfBand) {
      out[i] = 255;
    } else if (hueDiff <= halfBand + falloff) {
      const tT = (hueDiff - halfBand) / falloff;
      out[i] = Math.round((1 - tT) * 255);
    } else {
      out[i] = 0;
    }
  }
  return out;
}

/** Luminance-band selection. Standard Photoshop curve: shadows
 *  peak at L≈0.15, midtones at L≈0.5, highlights at L≈0.85; each
 *  band's width widens with fuzziness. */
function buildLuminanceBandMask(
  data: Uint8ClampedArray,
  out: Uint8Array,
  mode: 'highlights' | 'midtones' | 'shadows',
  fuzziness: number,
): Uint8Array {
  const center = mode === 'shadows' ? 0.15 : mode === 'highlights' ? 0.85 : 0.5;
  // Base half-width 0.15 (15%); fuzziness widens up to +0.25.
  const halfBand = 0.15 + (fuzziness / 200) * 0.25;
  const falloff = 0.05 + (fuzziness / 200) * 0.15;

  const len = out.length;
  for (let i = 0; i < len; i++) {
    const di = i * 4;
    // Per-channel luminance via Rec. 709 weights — closer to perceived
    // brightness than the HSL `L`. The hue-band path doesn't need
    // perceptual luma so it stays on HSL for code-share.
    const luma = (0.2126 * data[di] + 0.7152 * data[di + 1] + 0.0722 * data[di + 2]) / 255;
    const lDiff = Math.abs(luma - center);
    if (lDiff <= halfBand) {
      out[i] = 255;
    } else if (lDiff <= halfBand + falloff) {
      const tT = (lDiff - halfBand) / falloff;
      out[i] = Math.round((1 - tT) * 255);
    } else {
      out[i] = 0;
    }
  }
  return out;
}

const HUE_BAND_CENTERS: Partial<Record<ColorRangeMode, number>> = {
  reds: 0,
  yellows: 60,
  greens: 120,
  cyans: 180,
  blues: 240,
  magentas: 300,
};

/** Public for tests. RGB 0..255 → [h 0..1, s 0..1, l 0..1]. */
export function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  return rgbToHslFast(r, g, b);
}

/** Returns a 3-tuple to avoid object allocation per pixel. Caller
 *  reads positionally. */
function rgbToHslFast(r: number, g: number, b: number): [number, number, number] {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  let max = rn;
  let min = rn;
  if (gn > max) max = gn;
  if (bn > max) max = bn;
  if (gn < min) min = gn;
  if (bn < min) min = bn;
  const l = (max + min) * 0.5;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) * 60;
  else if (max === gn) h = ((bn - rn) / d + 2) * 60;
  else h = ((rn - gn) / d + 4) * 60;
  return [h / 360, s, l];
}

/** Wrapped hue distance in degrees, in [0, 180]. */
function hueDistanceDeg(a: number, b: number): number {
  let d = Math.abs(a - b) % 360;
  if (d > 180) d = 360 - d;
  return d;
}

/** HSL-space distance scaled to [0, 1]ish. Hue weighted lighter
 *  for desaturated pixels (where hue is unreliable). */
function hslDistance(
  h1: number,
  s1: number,
  l1: number,
  h2: number,
  s2: number,
  l2: number,
): number {
  // Hue wrap (h is normalized 0..1).
  let dh = Math.abs(h1 - h2);
  if (dh > 0.5) dh = 1 - dh;
  // Reduce hue contribution when either sample is desaturated.
  const sMin = Math.min(s1, s2);
  const dhEff = dh * sMin;
  const ds = Math.abs(s1 - s2);
  const dl = Math.abs(l1 - l2);
  // Weights: luma > sat > hue. Hue is doubled because dh is in
  // [0, 0.5] while ds/dl are in [0, 1] — without the 2x, hue would
  // be undercounted.
  return Math.sqrt(2 * dhEff * dhEff + 0.6 * ds * ds + 0.4 * dl * dl);
}
